/**
 * Dashboard do cliente: cards de resumo (consumo, gasto, geração, economia), gráficos de 13 meses e oportunidades de economia. Todos os dados vêm do Redux hidratado no login (App.tsx) — a página não faz fetch próprio; seleciona a UC ativa e deriva os agregados das faturas.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { clickable } from 'utils/a11y';
import { useNavigate } from 'react-router-dom';
import {
  BsFunnelFill,
  BsChevronCompactRight,
  BsChevronCompactLeft,
  BsPlusLg,
  BsArrowRight,
} from 'react-icons/bs';

import { ConsumerUnit } from 'entities/ConsumerUnit';
import { Invoice } from 'entities/Invoice';

import { filterInvoiceList } from 'utils/filter_invoice_list';
import { sortConsumerUnits } from 'utils/consumerUnitOrder';
import { useConsumerUnitReorder } from 'hooks/useConsumerUnitReorder';
import { getFilterRefMonth } from 'utils/filter_consumer_unit';
import {
  getMonthQuantityBetweenRefMonth,
  getRefMonthList,
  shiftRefMonthBack,
} from 'utils/ref_month';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import * as selectUser from 'store/user/user.selector';
import {
  selectConsumerUnitList,
  selectSelectedConsumerUnitIds,
} from 'store/consumer_unit/consumer_unit.selector';
import {
  clearSelectedConsumerUnitIds,
  setConsumerUnitRefresh,
} from 'store/consumer_unit/consumer_unit.slice';
import { setInvoiceRefresh } from 'store/invoice/invoice.slice';
import { setModalInvoiceState } from 'store/modal_invoice/modal_invoice.slice';
import {
  selectFilterEndDate,
  selectFilterMonthSelected,
  selectFilterPeriod,
  selectFilterStartDate,
  selectFilterState,
  selectFilterUtilityCompanyId,
} from 'store/filter_consumer_unit/filter_consumer_unit.selector';

import { selectInvoiceList } from 'store/invoice/invoice.selector';

import { ConsumerUnitCard } from './components/consumer-unit-card/consumer-unit-card.component';
import { DigestCard } from './components/digest-card/digest-card.component';
import { MeanConsumption } from './components/mean-consumption/mean-consumption.component';
import { EnvironmentResults } from './components/enviroment-results/enviroment-results.component';
import { ConsumptionForecastCard } from './components/consumption-forecast/consumption-forecast.component';
// "Economize até" para todos (out/2026): admin soma as oportunidades do motor
// (`/analysis` segue admin no backend); o cliente vê a estimativa de referência
// — a mesma da tela de Oportunidades dele.
import { EconomyOportunities } from './components/economy-opportunities/economy-oportunities.component';
import { Filter } from './components/filter/filter.component';
import { ModalPresentation } from './components/modal-welcome/modal-welcome.component';
import { OnboardingChecklist } from './components/onboarding-checklist/onboarding-checklist.component';
import {
  PlanUpgradeCard,
  usePlanUpgradeVisible,
} from './components/plan-upgrade-card/plan-upgrade-card.component';

import {
  DashboardContainer,
  Content,
  ConsumerUnitFilterCount,
  ConsumerUnitListContainer,
  ConsumerUnitControls,
  ConsumerUnitControl,
  ConsumerUnitList,
  NoConsumerUnits,
  EmptyAddCard,
  EmptyAddIcon,
  EmptyPromoCard,
  EmptyPromoText,
  EmptyPromoButton,
  ConsumerUnitSelectionContainer,
  Alert,
  EconomyOpportunitiesContainer,
} from './dashboard.styles';
import { dataCompleteness } from 'utils/managerIndicators';

const PENDING_DISMISS_STORAGE = 'dashboard:pendingInvoicesDismissed';
const pendingSignature = (
  pending: { name: string; missing: string[] }[],
  months: string[]
) =>
  `${months[0] ?? ''}-${months[months.length - 1] ?? ''}:${pending
    .map((p) => `${p.name}=${p.missing.join(',')}`)
    .sort()
    .join('|')}`;

const monthBr = (rm: string) => `${rm.slice(5, 7)}/${rm.slice(0, 4)}`;

const Dashboard = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [filteredConsumerUnit, setFilteredConsumerUnit] = useState<
    ConsumerUnit[]
  >([]);
  // UCs com fatura faltando no período (nome + meses) — aviso laranja dispensável
  const [pendingUnits, setPendingUnits] = useState<
    { name: string; missing: string[] }[]
  >([]);
  const [pendingDismissedKey, setPendingDismissedKey] = useState<string | null>(
    () => {
      try {
        return localStorage.getItem(PENDING_DISMISS_STORAGE);
      } catch {
        return null;
      }
    }
  );
  const [refMonthList, setRefMonthList] = useState<string[]>([]);
  const [filteredInvoiceList, setFilteredInvoiceList] = useState<Invoice[]>([]);
  const [prevFilteredInvoiceList, setPrevFilteredInvoiceList] = useState<
    Invoice[]
  >([]);
  const [monthQuantity, setMonthQuantity] = useState<number>(12);

  const consumerUnitList = useAppSelector(selectConsumerUnitList);
  const invoiceList = useAppSelector(selectInvoiceList);
  const currentUser = useAppSelector(selectUser.currentUser);
  const showPlanUpgrade = usePlanUpgradeVisible();
  // FILTERS
  const selectedConsumerUnits = useAppSelector(selectSelectedConsumerUnitIds);
  const filterEstablishmentId = useAppSelector(selectFilterState);
  const filterUtilityCompanyId = useAppSelector(selectFilterUtilityCompanyId);
  const filterPeriod = useAppSelector(selectFilterPeriod);
  const filterMonthSelected = useAppSelector(selectFilterMonthSelected);
  const filterStartDate = useAppSelector(selectFilterStartDate);
  const filterEndDate = useAppSelector(selectFilterEndDate);
  const consumerUnitIdList = useAppSelector(selectSelectedConsumerUnitIds);

  useEffect(() => {
    dispatch(setConsumerUnitRefresh({ refresh: true }));
    dispatch(setInvoiceRefresh({ refresh: true }));
  }, []);

  useEffect(() => {
    // Favoritos primeiro e a ordem escolhida pelo cliente (out/2026)
    const sortedList = sortConsumerUnits(consumerUnitList);
    const baseList =
      filterEstablishmentId === 'all'
        ? sortedList
        : sortedList.filter(
            (x) => x.establishment?.id === filterEstablishmentId
          );

    const newFilteredConsumerUnitList: ConsumerUnit[] = baseList.map((x) => {
      const unitInvoiceList = filteredInvoiceList.filter(
        (y) => y.consumerUnit.id === x.id
      );
      const consumerUnit: ConsumerUnit = {
        ...x,
        invoiceList: unitInvoiceList,
      };

      return consumerUnit;
    });

    // Aviso "Faltam faturas no período" — mesma regra do indicador "Faturas
    // faltando" das Análises (`dataCompleteness`): não conta o ÚLTIMO mês do
    // período (a conta pode ainda não ter chegado — decisão do Bruno
    // 30/set/2026) nem os meses ANTERIORES à 1ª fatura do imóvel. Out/2026 (relato
    // do Bruno: aviso com 9 de 10 faturas enviadas): o imóvel cadastrado no meio
    // do período era cobrado pelos meses em que ainda não existia, e com o
    // filtro de distribuidora os imóveis das outras apareciam como "sem fatura".
    const unitsInScope =
      filterUtilityCompanyId === 'all'
        ? newFilteredConsumerUnitList
        : newFilteredConsumerUnitList.filter(
            (u) => u.utilityCompany?.id === filterUtilityCompanyId
          );
    setPendingUnits(
      dataCompleteness(unitsInScope, invoiceList, refMonthList)
        .filter((r) => r.missingMonths.length > 0)
        .map((r) => ({ name: r.unitName, missing: r.missingMonths }))
    );

    setFilteredConsumerUnit(newFilteredConsumerUnitList);
  }, [
    consumerUnitList,
    filteredInvoiceList,
    refMonthList,
    filterEstablishmentId,
    filterUtilityCompanyId,
    invoiceList,
  ]);

  useEffect(() => {
    const [startRefMonth, endRefMonth] = getFilterRefMonth(
      filterPeriod,
      filterMonthSelected,
      filterStartDate,
      filterEndDate
    );

    const diffMonth = getMonthQuantityBetweenRefMonth(
      startRefMonth,
      endRefMonth
    );

    const refMonthList = getRefMonthList(startRefMonth, endRefMonth);

    setRefMonthList(refMonthList);
    setMonthQuantity(diffMonth);

    const newInvoiceList = filterInvoiceList(
      invoiceList,
      consumerUnitIdList,
      filterUtilityCompanyId,
      startRefMonth,
      endRefMonth
    );

    setFilteredInvoiceList(newInvoiceList);

    // Período anterior (mesmo intervalo, deslocado para trás)
    const prevEndRefMonth = shiftRefMonthBack(startRefMonth, 1);
    const prevStartRefMonth = shiftRefMonthBack(startRefMonth, diffMonth);
    const prevInvoiceList = filterInvoiceList(
      invoiceList,
      consumerUnitIdList,
      filterUtilityCompanyId,
      prevStartRefMonth,
      prevEndRefMonth
    );
    setPrevFilteredInvoiceList(prevInvoiceList);
  }, [
    invoiceList,
    filterUtilityCompanyId,
    filterPeriod,
    filterMonthSelected,
    filterStartDate,
    filterEndDate,
    consumerUnitIdList,
  ]);

  // Arrastar os cards do carrossel reordena os imóveis (mesma ordem da
  // lista de Imóveis)
  const orderedIds = useMemo(
    () => sortConsumerUnits(consumerUnitList).map((x) => x.id),
    [consumerUnitList]
  );
  const reorder = useConsumerUnitReorder(orderedIds);

  const handleClearSelectedConsumerUnits = () => {
    dispatch(clearSelectedConsumerUnitIds());
  };

  return (
    <DashboardContainer>
      <Alert
        show={
          pendingUnits.length > 0 &&
          pendingDismissedKey !== pendingSignature(pendingUnits, refMonthList)
        }
        onClose={() => {
          // Dispensa lembrada até mudar o conjunto de UCs/meses em falta
          const key = pendingSignature(pendingUnits, refMonthList);
          setPendingDismissedKey(key);
          try {
            localStorage.setItem(PENDING_DISMISS_STORAGE, key);
          } catch {
            /* sem storage: dispensa só nesta visita */
          }
        }}
        dismissible
      >
        <b>Faltam faturas no período.</b>{' '}
        {pendingUnits
          .map((p) => `"${p.name}": ${p.missing.map(monthBr).join(', ')}`)
          .join(' · ')}
        . Os totais abaixo consideram só as faturas enviadas.
      </Alert>
      <OnboardingChecklist />

      <Filter />
      {consumerUnitList.length > 0 && (
        <ConsumerUnitFilterCount>
          <BsFunnelFill size={18} /> {consumerUnitList?.length} unidades
          consumidoras
        </ConsumerUnitFilterCount>
      )}
      {consumerUnitList.length === 0 && (
        <NoConsumerUnits>
          <EmptyAddCard
            {...clickable(
              () =>
                dispatch(
                  setModalInvoiceState({ modalState: 'select_invoice' })
                ),
              'Adicionar imóvel'
            )}
          >
            <h4>Adicionar imóvel</h4>
            <p>
              Comece inserindo seu <strong>PRIMEIRO</strong> imóvel
            </p>
            <EmptyAddIcon>
              <BsPlusLg />
            </EmptyAddIcon>
          </EmptyAddCard>
          <EmptyPromoCard>
            <EmptyPromoText>
              <p>Tenha mais controle do seu</p>
              <h2>Gasto com energia</h2>
              <span>
                Faça o upgrade do seu plano Energypass e habilite o melhor que
                nós temos a oferecer.
              </span>
              <EmptyPromoButton onClick={() => navigate('/conta')}>
                Comparar planos <BsArrowRight />
              </EmptyPromoButton>
            </EmptyPromoText>
          </EmptyPromoCard>
        </NoConsumerUnits>
      )}

      <Content className="mb-4">
        {consumerUnitList.length > 0 && (
          <>
            <ConsumerUnitListContainer id="consumer-unit-list-container">
              <ConsumerUnitList className="d-grid ">
                {filteredConsumerUnit.map((consumerUnit) => (
                  <ConsumerUnitCard
                    consumerUnit={consumerUnit}
                    invoiceList={invoiceList}
                    monthQuantity={monthQuantity}
                    key={consumerUnit.id}
                    refMonthList={refMonthList}
                    dragProps={reorder.bind(consumerUnit.id)}
                    isDragOver={reorder.overId === consumerUnit.id}
                  />
                ))}
              </ConsumerUnitList>
            </ConsumerUnitListContainer>
            <ConsumerUnitControls>
              <ConsumerUnitControl
                id="next"
                {...clickable(() => {
                  const scroll = document.getElementById(
                    'consumer-unit-list-container'
                  );
                  const cards = document.querySelector<HTMLElement>(
                    '.consumer-unit-card:first-of-type'
                  )?.offsetWidth;
                  scroll!.scrollLeft += (cards || 0) + 16;
                  const selection = document.getSelection();
                  selection?.removeAllRanges();
                }, 'Próximos imóveis')}
              >
                <BsChevronCompactRight size={32} color="#999999" />
              </ConsumerUnitControl>
              <ConsumerUnitControl
                id="previous"
                {...clickable(() => {
                  const scroll = document.getElementById(
                    'consumer-unit-list-container'
                  );
                  const cards = document.querySelector<HTMLElement>(
                    '.consumer-unit-card:first-of-type'
                  )?.offsetWidth;
                  scroll!.scrollLeft -= (cards || 0) + 16;
                  const selection = document.getSelection();
                  selection?.removeAllRanges();
                }, 'Imóveis anteriores')}
              >
                <BsChevronCompactLeft size={32} color="#999999" />
              </ConsumerUnitControl>
            </ConsumerUnitControls>
          </>
        )}
        {selectedConsumerUnits.length > 0 && (
          <ConsumerUnitSelectionContainer>
            <p>
              Visualizando informações de{' '}
              {selectedConsumerUnits.length > 1 ? (
                <b>{selectedConsumerUnits.length} unidades selecionadas</b>
              ) : (
                <b>1 unidade selecionada</b>
              )}
            </p>
            <span {...clickable(handleClearSelectedConsumerUnits)}>
              limpar seleção
            </span>
          </ConsumerUnitSelectionContainer>
        )}
        <DigestCard
          invoiceList={
            !selectedConsumerUnits.length
              ? filteredInvoiceList
              : filteredInvoiceList.filter((x) =>
                  selectedConsumerUnits.includes(x.consumerUnit.id)
                )
          }
          prevInvoiceList={
            !selectedConsumerUnits.length
              ? prevFilteredInvoiceList
              : prevFilteredInvoiceList.filter((x) =>
                  selectedConsumerUnits.includes(x.consumerUnit.id)
                )
          }
          consumerUnitList={filteredConsumerUnit}
        />
        <MeanConsumption
          consumerUnitList={
            !selectedConsumerUnits.length
              ? filteredConsumerUnit
              : filteredConsumerUnit.filter((x) =>
                  selectedConsumerUnits.includes(x.id)
                )
          }
          invoiceList={
            !selectedConsumerUnits.length
              ? filteredInvoiceList
              : filteredInvoiceList.filter((x) =>
                  selectedConsumerUnits.includes(x.consumerUnit.id)
                )
          }
          invoiceRefMonthList={refMonthList}
        />
        <EnvironmentResults
          invoiceList={
            !selectedConsumerUnits.length
              ? filteredInvoiceList
              : filteredInvoiceList.filter((x) =>
                  selectedConsumerUnits.includes(x.consumerUnit.id)
                )
          }
        />
        {/* Tendência + previsão de gasto de todas as UCs — carrega sob demanda
            (ver comentário no componente: ~3 computeAudit por UC). */}
        <ConsumptionForecastCard />
        {/* "Economize até" (cobranças a mais + oportunidades de todos os
            imóveis → tela de Oportunidades) e, ao lado, o "Comparar planos"
            do Figma para o dono da conta fora do Enterprise. */}
        <EconomyOpportunitiesContainer>
          <EconomyOportunities />
          {showPlanUpgrade && <PlanUpgradeCard />}
        </EconomyOpportunitiesContainer>
      </Content>
      <ModalPresentation />
    </DashboardContainer>
  );
};

export default Dashboard;
